import { logger } from "@/utils/debug";

/**
 * Deletes the entire atlasio-store database
 * This will delete ALL data and recreate the database with correct stores on next access
 *
 * Usage:
 * ```ts
 * await atlasio.clearDatabase()
 * ```
 */
export async function clearOldStores(): Promise<void> {
  const dbName = "atlasio-store";

  return new Promise((resolve, reject) => {
    // Close any open connections first
    try {
      // Try to close any existing connections by attempting to open and close
      const closeRequest = indexedDB.open(dbName);
      closeRequest.onsuccess = () => {
        closeRequest.result.close();
        proceedWithDelete();
      };
      closeRequest.onerror = () => {
        // If we can't open, just proceed with delete
        proceedWithDelete();
      };
    } catch (e) {
      // If anything fails, just proceed with delete
      proceedWithDelete();
    }

    function proceedWithDelete() {
      const timeout = setTimeout(() => {
        logger.error(
          "[clearDatabase] Delete timeout - database may be locked. Close all tabs and try again."
        );
        reject(
          new Error(
            "Database deletion timed out. Close all browser tabs and try again."
          )
        );
      }, 5000);

      const deleteRequest = indexedDB.deleteDatabase(dbName);

      deleteRequest.onerror = () => {
        clearTimeout(timeout);
        logger.error(
          "[clearDatabase] Error deleting database:",
          deleteRequest.error
        );
        reject(deleteRequest.error || new Error("Failed to delete database"));
      };

      deleteRequest.onsuccess = () => {
        clearTimeout(timeout);
        resolve();
      };

      deleteRequest.onblocked = () => {
        logger.warn(
          "[clearDatabase] Database deletion blocked - close other tabs"
        );
        // Wait a bit and retry
        setTimeout(() => {
          const retryRequest = indexedDB.deleteDatabase(dbName);
          retryRequest.onsuccess = () => {
            clearTimeout(timeout);
            resolve();
          };
          retryRequest.onerror = () => {
            clearTimeout(timeout);
            reject(
              retryRequest.error ||
                new Error("Failed to delete database on retry")
            );
          };
        }, 1000);
      };
    }
  });
}

/**
 * Lists all stores in the atlasio-store database
 * Useful for debugging and seeing what stores exist
 *
 * Usage:
 * ```ts
 * import { listAllStores } from "@/services/storage";
 * import { logger } from "@/utils/debug";
 *
 * const stores = await listAllStores();
 * logger.log("Current stores:", stores);
 * ```
 */
export async function listAllStores(): Promise<string[]> {
  const dbName = "atlasio-store";

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onerror = () => {
      reject(request.error || new Error("Failed to open database"));
    };

    request.onsuccess = () => {
      const db = request.result;
      const stores = Array.from(db.objectStoreNames);
      db.close();
      resolve(stores);
    };
  });
}
